import { MATERIAL_TYPES, MATERIAL_UNITS, suggestedMaterialType } from '@felix/domain';
import { z } from 'zod';
import { signedQuantity } from './inventory.js';
import type {
  Material,
  MaterialCategory,
  MaterialInput,
  StockItem,
  StockItemInput,
  StockMovementInput,
} from './types.js';

/**
 * Malzeme ve stok formları (React Hook Form + Zod; CLAUDE.md "Form ve doğrulama"). Kurallar
 * sunucuda `@felix/domain` şemalarıyla yeniden denetlenir; burada kullanıcıya erken söylenir.
 * Hata iletileri çeviri anahtarıdır (`materials.form.errors.*`).
 */

const E = (code: string) => `materials.form.errors.${code}`;
const MAX_QTY = 1_000_000_000;

/** Katalog birimleri ve türleri `@felix/domain` ile aynı (kumanya birimleri ve türü; ADR 0099 §3–§4). */
export const MATERIAL_UNIT_OPTIONS = MATERIAL_UNITS;
export const MATERIAL_TYPE_OPTIONS = MATERIAL_TYPES;

const optionalText = (max: number) => z.string().max(max, E('tooLong'));

export const materialFormSchema = z
  .object({
    categoryId: z.string().min(1, E('required')),
    secondaryCategoryId: z.string(),
    name: z.string().trim().min(1, E('required')).max(200, E('tooLong')),
    specification: optionalText(200),
    unit: z.enum(MATERIAL_UNIT_OPTIONS, { message: E('required') }),
    materialType: z.enum(MATERIAL_TYPE_OPTIONS, { message: E('required') }),
    companyCode: optionalText(60),
    impaCode: optionalText(20),
    issaCode: optionalText(20),
    critical: z.boolean(),
    notes: optionalText(2000),
  })
  .superRefine((values, ctx) => {
    if (values.secondaryCategoryId && values.secondaryCategoryId === values.categoryId) {
      ctx.addIssue({ code: 'custom', message: E('sameCategory'), path: ['secondaryCategoryId'] });
    }
  });
export type MaterialFormValues = z.input<typeof materialFormSchema>;

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

export function emptyMaterialForm(categoryId = ''): MaterialFormValues {
  return {
    categoryId,
    secondaryCategoryId: '',
    name: '',
    specification: '',
    unit: 'PCS',
    materialType: 'Consumable',
    companyCode: '',
    impaCode: '',
    issaCode: '',
    critical: false,
    notes: '',
  };
}

/**
 * Yeni kalemin önerilen türü (ADR 0122 §2): "Provisions" ana kategorisi ya da IMPA 00 bölümü
 * `Provision`; yoksa formun varsayılanı. Kullanıcı türü kendisi seçtiyse form öneriyi uygulamaz.
 */
export function suggestedFormType(
  values: Pick<MaterialFormValues, 'categoryId' | 'impaCode'>,
  categories: readonly MaterialCategory[],
): MaterialFormValues['materialType'] {
  const mainCategory = categories.find((c) => c.id === values.categoryId)?.mainCategory ?? null;
  return (
    suggestedMaterialType({ impaCode: values.impaCode, mainCategory }) ??
    emptyMaterialForm().materialType
  );
}

export function materialFormOf(material: Material): MaterialFormValues {
  return {
    categoryId: material.categoryId,
    secondaryCategoryId: material.secondaryCategoryId ?? '',
    name: material.name,
    specification: material.specification ?? '',
    unit: material.unit as MaterialFormValues['unit'],
    materialType: material.materialType as MaterialFormValues['materialType'],
    companyCode: material.companyCode ?? '',
    impaCode: material.impaCode ?? '',
    issaCode: material.issaCode ?? '',
    critical: material.critical,
    notes: material.notes ?? '',
  };
}

export function toMaterialInput(values: MaterialFormValues): MaterialInput {
  return {
    categoryId: values.categoryId,
    secondaryCategoryId: blank(values.secondaryCategoryId),
    name: values.name.trim(),
    specification: blank(values.specification),
    unit: values.unit,
    materialType: values.materialType,
    companyCode: blank(values.companyCode),
    impaCode: blank(values.impaCode),
    issaCode: blank(values.issaCode),
    critical: values.critical,
    notes: blank(values.notes),
  };
}

/** Sayı alanı: Mantine NumberInput boşken `''` verir. */
const quantity = z.union([z.number(), z.literal('')]);

export const stockItemFormSchema = z
  .object({
    materialId: z.string().min(1, E('required')),
    location: optionalText(200),
    minQty: quantity,
    maxQty: quantity,
    reorderLevel: quantity,
    /**
     * Eldeki miktar (ADR 0141 §3): yalnız yeni kalemde; boş ya da sıfır açılış girişi yazmaz.
     * İsteğe bağlı: eski kayıtlı form değerleri ve düzenleme bu alanı taşımaz.
     */
    openingQty: quantity.optional(),
  })
  .superRefine((values, ctx) => {
    const issue = (path: string, code: string) =>
      ctx.addIssue({ code: 'custom', message: E(code), path: [path] });
    if (values.minQty === '') issue('minQty', 'required');
    for (const key of ['minQty', 'maxQty', 'reorderLevel', 'openingQty'] as const) {
      const value = values[key];
      if (value !== undefined && value !== '' && (value < 0 || value > MAX_QTY)) {
        issue(key, 'outOfRange');
      }
    }
    if (values.minQty !== '' && values.maxQty !== '' && values.minQty > values.maxQty) {
      issue('maxQty', 'minAboveMax');
    }
  });
export type StockItemFormValues = z.input<typeof stockItemFormSchema>;

export function emptyStockItemForm(): StockItemFormValues {
  return {
    materialId: '',
    location: '',
    minQty: '',
    maxQty: '',
    reorderLevel: '',
    openingQty: '',
  };
}

export function stockItemFormOf(item: StockItem): StockItemFormValues {
  return {
    materialId: item.materialId,
    location: item.location ?? '',
    minQty: item.minQty,
    maxQty: item.maxQty ?? '',
    reorderLevel: item.reorderLevel ?? '',
  };
}

export function toStockItemInput(values: StockItemFormValues): StockItemInput {
  const number = (value: number | '') => (value === '' ? null : value);
  return {
    materialId: values.materialId,
    location: blank(values.location),
    minQty: values.minQty === '' ? 0 : values.minQty,
    maxQty: number(values.maxQty),
    reorderLevel: number(values.reorderLevel),
    ...(typeof values.openingQty === 'number' && values.openingQty > 0
      ? { openingQty: values.openingQty }
      : {}),
  };
}

export const MOVEMENT_KINDS = ['in', 'out', 'adjust'] as const;

/**
 * Stok hareketi formu: giriş ve çıkışta miktar artı girilir, işaret türden gelir; düzeltmede
 * işaretli miktar ve gerekçe zorunlu (ADR 0026 §2). Gün gemi saatiyle; gelecek olamaz.
 */
export function movementFormSchema(today: string) {
  return z
    .object({
      stockItemId: z.string().min(1, E('required')),
      kind: z.enum(MOVEMENT_KINDS),
      quantity,
      localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, E('required')),
      reason: optionalText(500),
    })
    .superRefine((values, ctx) => {
      const issue = (path: string, code: string) =>
        ctx.addIssue({ code: 'custom', message: E(code), path: [path] });
      if (values.quantity === '') issue('quantity', 'required');
      else if (values.quantity === 0) issue('quantity', 'zero');
      else if (values.kind !== 'adjust' && values.quantity < 0) issue('quantity', 'positive');
      else if (Math.abs(values.quantity) > MAX_QTY) issue('quantity', 'outOfRange');
      if (values.kind === 'adjust' && values.reason.trim() === '') issue('reason', 'required');
      if (values.localDate > today) issue('localDate', 'future');
    });
}
export type MovementFormValues = z.input<ReturnType<typeof movementFormSchema>>;

export function emptyMovementForm(
  today: string,
  kind: 'in' | 'out' | 'adjust' = 'in',
  stockItemId = '',
): MovementFormValues {
  return { stockItemId, kind, quantity: '', localDate: today, reason: '' };
}

export function toMovementInput(values: MovementFormValues, today: string): StockMovementInput {
  const amount = values.quantity === '' ? 0 : values.quantity;
  return {
    stockItemId: values.stockItemId,
    kind: values.kind,
    quantity: signedQuantity(values.kind, amount),
    reason: blank(values.reason),
    ...(values.localDate === today ? {} : { localDate: values.localDate }),
  };
}
